import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Select,
  useColorMode,
  useToast,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  Spinner,
  Link,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { QuestionIcon } from "@chakra-ui/icons";
import { cqAPI } from "../../lib/Auth";

function HrSettings() {
  const [hrSettings, setHrSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const toast = useToast();

  const timeouts = useRef({});

  useEffect(() => {
    fetchHrSettings();
  }, []);

  const fetchHrSettings = async () => {
    try {
      const response = await cqAPI.get("/settings/hr");
      setHrSettings(response.data);
      setIsLoading(false);
    } catch (error) {
      toast({
        title: "Error fetching HR settings",
        description: error.message || "An error occurred",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  const updateHrSetting = async (field, value, bid) => {
    try {
      const response = await cqAPI.post(`/settings/hr`, {
        field: field,
        value: value,
      });
      if (response.type === "error") {
        toast({
          title: "Error updating HR settings",
          description: response.msg || "An error occurred",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }
      // Optionally, you can fetch the updated settings here
      // fetchHrSettings();
    } catch (error) {
      toast({
        title: "Error updating HR settings",
        description: error.message || "An error occurred",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  // Debounce function to prevent rapid API calls
  const debouncedUpdateHrSetting = (field, value) => {
    if (timeouts.current[field]) {
      clearTimeout(timeouts.current[field]);
    }
    timeouts.current[field] = setTimeout(() => {
      updateHrSetting(field, value);
      delete timeouts.current[field];
    }, 500); // Adjust debounce delay as needed
  };

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeouts.current).forEach(clearTimeout);
    };
  }, []);

  if (isLoading) {
    return <Spinner />;
  }

  if (!hrSettings) {
    return <Text>No HR settings found</Text>;
  }

  const hr = hrSettings.hr || {};
  const monthlyBonus = hrSettings.monthly_bonus || {};
  const activePlan = hrSettings.active_plan || {};

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
      textAlign="left"
    >
      {/* Payroll and time-tracking Section */}
      <Box width="100%" p={4}>
        <Box
          p={4}
          bgColor={colorMode === "light" ? "gray.100" : "gray.700"}
          borderRadius="md"
          textAlign="left"
        >
          <Heading size="md" mb={4}>
            Payroll and time-tracking
          </Heading>
          <Text mb={4}>
            These settings are used when calculating the payroll.
          </Text>
          <Box>
            {hrSettings.parent && (
              <Box mb={4}>
                <Checkbox
                  isChecked={hr.use_root}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setHrSettings({
                      ...hrSettings,
                      hr: {
                        ...hr,
                        use_root: newValue,
                      },
                    });
                    debouncedUpdateHrSetting("hr.use_root", newValue);
                  }}
                >
                  Use the group settings
                </Checkbox>
                <Text mt={2}>
                  This setting is{" "}
                  <Link
                    onClick={() => {
                      navigate("/settings/hr?bid=" + hrSettings.parent);
                    }}
                  >
                    here
                  </Link>
                  .
                </Text>
              </Box>
            )}
            {(!hrSettings.parent || !hr.use_root) && (
              <Box>
                <FormControl mb={4}>
                  <FormLabel>Payroll Frequency</FormLabel>
                  <Select
                    value={hr.frequency || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setHrSettings({
                        ...hrSettings,
                        hr: {
                          ...hr,
                          frequency: newValue,
                        },
                      });
                      debouncedUpdateHrSetting("hr.frequency", newValue);
                    }}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-Weekly</option>
                  </Select>
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel display="flex" alignItems="center">
                    First Day of work in a payroll
                    <Tooltip
                      label="The first day of the payroll. It's the date that counts as the first day of work from the first payroll. The payroll will include this day (the previous payroll would have been until the day before)"
                      fontSize="md"
                    >
                      <QuestionIcon ml={2} />
                    </Tooltip>
                  </FormLabel>
                  <Input
                    type="text"
                    placeholder="MM/DD/YYYY"
                    pattern="(^$|^(0[1-9]|[1-9]|1[0-2])/(0[1-9]|[1-9]|[1-2][0-9]|3[0-1])/(\d{4})$)"
                    value={hr.first_date || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setHrSettings({
                        ...hrSettings,
                        hr: {
                          ...hr,
                          first_date: newValue,
                        },
                      });
                      debouncedUpdateHrSetting("hr.first_date", newValue);
                    }}
                  />
                </FormControl>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {/* Employee bonus tracking Section */}
      <Box width="100%" p={4}>
        <Box
          p={4}
          bgColor={colorMode === "light" ? "gray.100" : "gray.700"}
          borderRadius="md"
          position="relative"
          textAlign="left"
        >
          <Heading size="md" mb={4}>
            Employee bonus tracking
          </Heading>
          <Text mb={4}>
            It's good practice to set a monthly office goal for your office.
            It's also good practice to reward your employees when they reach a
            monthly goal.
            <br />
            A really good way of setting the goal is to base it on the monthly
            average of collection and production. We create a bonus pool for
            your office based on your parameters and reward your staff
            proportionately to the time that they worked in the office during
            the month.
            <br />
            Set your goal, and we will keep track of everything.
          </Text>
          <Box>
            <Checkbox
              isChecked={monthlyBonus.enabled}
              onChange={(e) => {
                const newValue = e.target.checked;
                setHrSettings({
                  ...hrSettings,
                  monthly_bonus: {
                    ...monthlyBonus,
                    enabled: newValue,
                  },
                });
                debouncedUpdateHrSetting("monthly_bonus.enabled", newValue);
              }}
            >
              Enable goal tracking for employees
            </Checkbox>
            {monthlyBonus.enabled && (
              <Box mt={4}>
                <FormControl mb={4}>
                  <FormLabel>Monthly goal target</FormLabel>
                  <Input
                    type="number"
                    pattern="^\d{1,}$"
                    value={hrSettings.monthly_goal || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setHrSettings({
                        ...hrSettings,
                        monthly_goal: newValue,
                      });
                      debouncedUpdateHrSetting("monthly_goal", newValue);
                    }}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>
                    The goal of the office is calculated based on production
                    and collection. You can make it be completely based on
                    production, collection, or even an average of the two.
                    Change the slider to get to the right ratio between the
                    two.
                  </FormLabel>
                  <Box display="flex" alignItems="center">
                    <Text mr={2}>Production</Text>
                    <Slider
                      min={0}
                      max={10}
                      step={1}
                      flex="1"
                      value={monthlyBonus.coefficient || 0}
                      onChange={(val) => {
                        setHrSettings({
                          ...hrSettings,
                          monthly_bonus: {
                            ...monthlyBonus,
                            coefficient: val,
                          },
                        });
                        debouncedUpdateHrSetting(
                          "monthly_bonus.coefficient",
                          val
                        );
                      }}
                    >
                      <SliderTrack>
                        <SliderFilledTrack />
                      </SliderTrack>
                      <SliderThumb />
                    </Slider>
                    <Text ml={2}>Collection</Text>
                  </Box>
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>
                    Portion of goal given to the bonus pool
                  </FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    pattern="^0\.\d{1,}$"
                    value={monthlyBonus.meet_goal || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setHrSettings({
                        ...hrSettings,
                        monthly_bonus: {
                          ...monthlyBonus,
                          meet_goal: newValue,
                        },
                      });
                      debouncedUpdateHrSetting(
                        "monthly_bonus.meet_goal",
                        newValue
                      );
                    }}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Bonus for reviews</FormLabel>
                  <Input
                    type="number"
                    pattern="^\d{1,}$"
                    value={monthlyBonus.reviews || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setHrSettings({
                        ...hrSettings,
                        monthly_bonus: {
                          ...monthlyBonus,
                          reviews: newValue,
                        },
                      });
                      debouncedUpdateHrSetting(
                        "monthly_bonus.reviews",
                        newValue
                      );
                    }}
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Bonus for referrals</FormLabel>
                  <Input
                    type="number"
                    pattern="^\d{1,}$"
                    value={monthlyBonus.referrals || ""}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setHrSettings({
                        ...hrSettings,
                        monthly_bonus: {
                          ...monthlyBonus,
                          referrals: newValue,
                        },
                      });
                      debouncedUpdateHrSetting(
                        "monthly_bonus.referrals",
                        newValue
                      );
                    }}
                  />
                </FormControl>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default HrSettings;
